import React, { useEffect, useState, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import { Delete } from '@mui/icons-material';
import { Tooltip, Box, Stack, Fade, IconButton } from '@mui/material';
import clsx from 'clsx';
import { ImageGalleryItem } from 'types';

interface ImageGalleryProps {
  height?: number;
  loading?: boolean;
  images: ImageGalleryItem[];
  onDelete?: (id: string) => any;
}

export const ImageGallery = ({ images, height = 400, onDelete, loading = false }: ImageGalleryProps) => {
  const classes = useStyles();

  const scrollRef = useRef<HTMLDivElement>(null);

  const [current, setCurrent] = useState<number>(0);
  const [fadeIn, setFadeIn] = useState<boolean>(true);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  if (images.length === 0) return null;

  const hasThumbnails = images.length > 1;

  const handleSelect = (index: number) => {
    if (index === current) return;

    setFadeIn(false);

    setTimeout(() => {
      setCurrent(index);
      setFadeIn(true);
    }, 150);
  };

  return (
    <Stack className={classes.root}>
      <Box className={classes.mainImageWrapper} sx={{ height }}>
        <Fade in={fadeIn} timeout={400}>
          <Box
            component="img"
            sx={{ height }}
            alt={`image-${current}`}
            src={images[current].url}
            className={classes.mainImage}
          />
        </Fade>
      </Box>

      {hasThumbnails && (
        <Box className={classes.thumbnails} ref={scrollRef}>
          {images.map(({ url, id }, index) => (
            <Box key={id} position="relative">
              <Box
                src={url}
                component="img"
                alt={`thumb-${index}`}
                onClick={() => handleSelect(index)}
                className={clsx(classes.thumb, { [classes.thumbActive]: current === index })}
              />

              {onDelete && (
                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    color="error"
                    disabled={loading}
                    sx={{ position: 'absolute', right: 0, top: 0 }}
                    onClick={async e => {
                      e.stopPropagation();
                      if (index === current) handleSelect(current - 1 > 0 ? current - 1 : 0);
                      await onDelete(id);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Stack>
  );
};

const useStyles = makeStyles({
  root: {
    gap: 16,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },

  mainImageWrapper: {
    width: '100%',
    maxWidth: 800,
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
  },

  mainImage: {
    top: 0,
    left: 0,
    height: 400,
    width: '100%',
    borderRadius: 8,
    objectFit: 'contain',
    position: 'absolute',
    transition: 'opacity 0.4s ease',
  },

  thumbnails: {
    gap: 8,
    width: '100%',
    maxWidth: 800,
    display: 'flex',
    overflowX: 'auto',
    padding: '0.5rem',
  },

  thumb: {
    width: '10rem',
    height: '7rem',
    borderRadius: 4,
    cursor: 'pointer',
    objectFit: 'contain',
    transition: 'all 0.2s ease',
    border: '2px solid transparent',

    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: '0 0 6px rgba(0,0,0,0.3)',
    },
  },

  thumbActive: {
    border: '2px solid #1976d2',
    boxShadow: '0 0 4px rgba(25,118,210,0.6)',
  },
});
